import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Bell, MagnifyingGlass, Plus, SignOut, User, ShieldCheck, ShieldStar, X,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);
  const [mobileSearch, setMobileSearch] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/me/notifications").then(({ data }) => setUnread(data.filter((n) => !n.read).length)).catch(() => {});
  }, [user]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/kesfet?q=${encodeURIComponent(q.trim())}`);
      setMobileSearch(false);
    }
  };

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-lg bg-white/85 border-b border-slate-100"
      data-testid="app-header"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-6 py-3.5 flex items-center gap-4 md:gap-8">
        <Link
          to="/"
          className="font-display text-2xl font-medium tracking-tight text-slate-900 shrink-0"
          data-testid="brand-logo"
        >
          kolaysatis<span className="text-[#B54037]">.</span>com
        </Link>

        {/* Desktop search — pill */}
        <form
          onSubmit={submit}
          className="flex-1 max-w-lg hidden md:flex items-center bg-slate-50 hover:bg-slate-100/60 focus-within:bg-white border border-transparent focus-within:border-slate-300 focus-within:soft-shadow rounded-full transition-all"
        >
          <MagnifyingGlass size={16} className="ml-4 text-slate-400" />
          <input
            data-testid="header-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Marka, model, kategori ara..."
            className="w-full bg-transparent px-3 py-2.5 outline-none text-sm placeholder:text-slate-400 text-slate-900"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="mr-3 p-1 rounded-full hover:bg-slate-100 text-slate-400"
              aria-label="Aramayı temizle"
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </form>

        <nav className="flex items-center gap-1 ml-auto">
          {/* Mobile: search icon */}
          <button
            onClick={() => setMobileSearch(true)}
            className="md:hidden p-2 rounded-full hover:bg-slate-50 transition-colors"
            aria-label="Ara"
            data-testid="mobile-search-toggle"
          >
            <MagnifyingGlass size={18} className="text-slate-700" />
          </button>

          <Link
            to="/kesfet"
            className="hidden sm:inline text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full transition-colors"
            data-testid="nav-explore"
          >
            Keşfet
          </Link>
          <Link
            to="/rehber"
            className="hidden md:inline text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-2 rounded-full transition-colors"
            data-testid="nav-blog"
          >
            Rehber
          </Link>

          {user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#B54037] px-3 py-2 rounded-full hover:bg-red-50 transition-colors"
                  title="Admin paneli"
                >
                  <ShieldStar size={15} weight="regular" /> Admin
                </Link>
              )}
              <Link
                to="/ilan-ver"
                className="bg-slate-900 text-white px-4 md:px-5 py-2.5 font-medium text-sm rounded-full hover:bg-slate-800 transition-colors flex items-center gap-1.5"
                data-testid="nav-create-auction"
              >
                <Plus size={15} weight="bold" /> <span className="hidden sm:inline">İlan ver</span>
              </Link>
              <Link
                to="/panelim"
                className="relative p-2 rounded-full hover:bg-slate-50 transition-colors ml-1"
                data-testid="nav-notifications"
              >
                <Bell size={18} className="text-slate-700" />
                {unread > 0 && (
                  <span className="absolute top-1 right-1 bg-[#B54037] text-white text-[9px] font-medium min-w-[15px] h-[15px] flex items-center justify-center px-1 rounded-full">
                    {unread}
                  </span>
                )}
              </Link>
              <Link
                to="/panelim"
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-slate-50 transition-colors"
                data-testid="nav-dashboard"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 object-cover rounded-full" />
                ) : (
                  <span className="w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs font-medium">
                    {user.name?.[0] || <User size={14} />}
                  </span>
                )}
                <span className="text-sm font-medium text-slate-800 hidden sm:inline">
                  {user.name.split(" ")[0]}
                </span>
                {user.is_verified && (
                  <ShieldCheck size={14} weight="fill" className="text-emerald-700 hidden sm:inline" />
                )}
              </Link>
              <button
                onClick={logout}
                className="p-2 rounded-full hover:bg-slate-50 transition-colors text-slate-400 hover:text-slate-900"
                title="Çıkış"
                data-testid="nav-logout"
              >
                <SignOut size={16} />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/giris"
                className="text-sm font-medium text-slate-700 px-3 py-2 rounded-full hover:bg-slate-50 transition-colors"
                data-testid="nav-login"
              >
                Giriş
              </Link>
              <Link
                to="/kayit"
                className="bg-slate-900 text-white px-4 md:px-5 py-2.5 font-medium text-sm rounded-full hover:bg-slate-800 transition-colors"
                data-testid="nav-register"
              >
                Üye ol
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* Mobile search overlay */}
      {mobileSearch && (
        <div className="md:hidden absolute inset-x-0 top-0 bg-white border-b border-slate-100 shadow-sm z-50" data-testid="mobile-search-bar">
          <form
            onSubmit={submit}
            className="max-w-[1280px] mx-auto px-4 py-3 flex items-center gap-2"
          >
            <div className="flex-1 flex items-center bg-slate-50 border border-slate-200 rounded-full">
              <MagnifyingGlass size={16} className="ml-4 text-slate-400" />
              <input
                data-testid="mobile-search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Marka, model, kategori ara..."
                className="w-full bg-transparent px-3 py-2.5 outline-none text-sm placeholder:text-slate-400 text-slate-900"
              />
            </div>
            <button
              type="button"
              onClick={() => setMobileSearch(false)}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-600"
              aria-label="Kapat"
              data-testid="mobile-search-close"
            >
              <X size={16} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </header>
  );
}
